import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { Save, X, ArrowLeft, Eye, Trash2, Maximize2, Minimize2, Folder } from 'lucide-react'
import { Link } from 'react-router-dom'
import { notesAPI } from '../../services/api'
import toast from 'react-hot-toast'
import { SYNC_CONFIG } from '../../services/syncConfig'
import frontendSync from '../../services/frontendSync'
import localStorageService from '../../services/localStorageService'
import { useTabs } from '../../contexts/TabsContext'
import { useNotes } from '../../contexts/NotesContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useSensitiveSession } from '../../contexts/SensitiveSessionContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import LoadingSpinner, { ButtonSpinner } from '../../components/common/LoadingSpinner'
import RichTextEditor from '../../components/common/RichTextEditor'
// Removed EditNotePasswordModal import - using browser popup instead
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts'
import { useStatus } from '../../contexts/StatusContext'

// Global cache for processed sensitive notes (shared across all component instances)
const globalProcessedAuthCache = new Map()

// Global password prompt tracking (shared across all component instances)
const globalPasswordPromptCache = new Map()

// Global component mount tracking to prevent duplicate mounts
const globalComponentMountCache = new Map()

// Global note data cache to share between component instances
const globalNoteDataCache = new Map()

// Global cache for decrypted content to prevent re-render loops
const globalDecryptedContentCache = new Map()

// Cleanup expired password prompt cache entries
const cleanupPasswordPromptCache = () => {
  const now = Date.now()
  const fiveMinutes = 300000 // 5 minutes
  
  for (const [key, value] of globalPasswordPromptCache.entries()) {
    if (now - value.timestamp > fiveMinutes) {
      globalPasswordPromptCache.delete(key)
    }
  }
}

// Clean up cache every 5 minutes
setInterval(cleanupPasswordPromptCache, 300000)

// Function to clean up expired cache entries
const cleanupExpiredCache = () => {
  const now = Date.now()
  const fiveMinutes = 300000
  for (const [key, value] of globalProcessedAuthCache.entries()) {
    if (now - value.timestamp > fiveMinutes) {
      globalProcessedAuthCache.delete(key)
    }
  }
}

/**
 * Edit Note Page Component
 * 
 * Form for editing existing notes with title and content.
 * Includes form validation, API integration, and delete functionality.
 * Can work in both standalone mode (with URL params) and tab mode (with props).
 */
const EditNotePage = ({ tabId = null, noteId: propNoteId = null }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: urlId } = useParams()
  const { tabs, openTab, closeTab, updateTab } = useTabs()
  const { fetchNotes, deleteNote, updateNote } = useNotes()
  const { folders, fetchFolders } = useFolders()
  const { isLocalMode, getLocalNote, getSensitivePassword, setSensitivePassword } = useLocalMode()
  const sensitiveSession = useSensitiveSession()
  const { showSuccess, showError, showWarning, showLoading, dismissStatus } = useStatus()
  
  // Use prop noteId if in tab mode, otherwise use URL param
  const noteId = propNoteId || urlId
  const isInTab = tabId !== null

  // Get current tab data to restore form state
  const currentTab = tabs.find(tab => tab.id === tabId)
  const savedFormData = currentTab?.formData
  const hasSavedChanges = savedFormData && (savedFormData.title?.trim() || savedFormData.content?.trim())

  /**
   * Check if content appears to be encrypted
   */
  const isContentEncrypted = (content) => {
    if (!content) return false
    
    // Check if content looks like encrypted data (AES-GCM produces base64-like strings)
    const looksLikeHTML = content.includes('<') && content.includes('>')
    const looksLikePlainText = content.length < 100 && /^[a-zA-Z0-9\s.,!?-]+$/.test(content)
    const looksLikeEncrypted = content.length > 50 && /^[A-Za-z0-9+/]*={0,2}$/.test(content)
    
    // If it looks like encrypted data and doesn't look like HTML or plain text, it's likely encrypted
    return looksLikeEncrypted && !looksLikeHTML && !looksLikePlainText
  }
  
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isPageLoading, setIsPageLoading] = useState(true)
  // Removed modal state - using browser popup instead
  const [pendingNote, setPendingNote] = useState(null)
  const [isPasswordForSave, setIsPasswordForSave] = useState(false)
  const loadingNotesRef = useRef(new Set())
  const passwordPromptRef = useRef(false)
  const [noteData, setNoteData] = useState(null)
  const [isNoteDecrypted, setIsNoteDecrypted] = useState(false)
  
  // Initialize isExpanded based on tab property or navigation state
  const [isExpanded, setIsExpanded] = useState(() => {
    if (isInTab && currentTab?.isExpanded) {
      return true // Tab has isExpanded property set
    }
    if (!isInTab && location.state?.isExpanded) {
      return true // Navigation state has isExpanded
    }
    return false // Default to not expanded
  })

  // Refs for form field focus management
  const titleInputRef = useRef(null)
  const contentEditorRef = useRef(null)
  const richTextEditorRef = useRef(null)
  
  // Refs to track previous values and prevent unnecessary updates
  const prevTitleRef = useRef(null)
  const prevFormDataRef = useRef(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    reset,
    control,
    setValue
  } = useForm({
    defaultValues: hasSavedChanges ? savedFormData : {
      title: '',
      content: ''
    }
  })

  // Watch form values for real-time updates
  const watchedTitle = watch('title')
  const watchedContent = watch('content')

  /**
   * Check if form has changes from original data
   */
  const hasChanges = useCallback(() => {
    if (!noteData) return false
    return (
      watchedTitle !== (noteData.title || '') ||
      watchedContent !== (noteData.content || '')
    )
  }, [watchedTitle, watchedContent, noteData])

  // Update tab dirty state when form changes
  useEffect(() => {
    if (isInTab && tabId && noteData) {
      const isDirty = hasChanges()
      updateTab(tabId, { isDirty })
    }
  }, [hasChanges, isInTab, tabId, noteData])

  // Update tab title dynamically as user types
  useEffect(() => {
    if (isInTab && tabId && watchedTitle !== undefined) {
      const title = watchedTitle?.trim() || 'Untitled Note'
      const newTitle = `Edit: ${title}`
      
      // Only update if the title has actually changed
      if (prevTitleRef.current !== newTitle) {
        prevTitleRef.current = newTitle
        updateTab(tabId, { title: newTitle })
      }
    }
  }, [watchedTitle, isInTab, tabId, updateTab])

  // Save form data to tab whenever user types
  useEffect(() => {
    if (isInTab && tabId && noteData && (watchedTitle !== undefined || watchedContent !== undefined)) {
      const formData = {
        title: watchedTitle || '',
        content: watchedContent || ''
      }
      
      // Only update if form data has actually changed
      const formDataString = JSON.stringify(formData)
      if (prevFormDataRef.current !== formDataString) {
        prevFormDataRef.current = formDataString
        updateTab(tabId, { formData })
      }
    }
  }, [watchedTitle, watchedContent, isInTab, tabId, noteData, updateTab])

  /**
   * Load note data when component mounts
   */
  useEffect(() => {
    console.log('🔍 EditNotePage: Component mounted with ID:', noteId)
    
    // Check if this note is already being processed by another component instance
    const mountKey = `mount-${noteId}`
    const noteDataKey = `noteData-${noteId}`
    const decryptedCacheKey = `decrypted-${noteId}`
    
    // Check if note data is already available in global cache
    if (globalNoteDataCache.has(noteDataKey)) {
      console.log('🔍 EditNotePage: Note data already available in global cache, using it')
      const cachedNoteData = globalNoteDataCache.get(noteDataKey)
      setNoteData(cachedNoteData)
      
      // Reset form with cached data
      const formDataToUse = hasSavedChanges ? savedFormData : {
        title: cachedNoteData.title || '',
        content: cachedNoteData.content || ''
      }
      
      // Only reset if the form data is different from current values
      const currentTitle = watch('title') || ''
      const currentContent = watch('content') || ''
      
      if (currentTitle !== formDataToUse.title || currentContent !== formDataToUse.content) {
        // Reset form with appropriate data
        setTimeout(() => {
          reset(formDataToUse)
        }, 100)
      }
      
      // Initialize tab's formData if user hasn't made changes yet
      if (isInTab && tabId && !hasSavedChanges) {
        updateTab(tabId, { 
          formData: formDataToUse,
          title: `Edit: ${cachedNoteData.title || 'Untitled Note'}`
        })
      } else if (isInTab && tabId && cachedNoteData.title) {
        updateTab(tabId, { title: `Edit: ${cachedNoteData.title}` })
      }
      
      setIsPageLoading(false)
      return
    }
    
    if (globalComponentMountCache.has(mountKey)) {
      console.log('🔍 EditNotePage: Note already being processed by another instance, waiting for completion...')
      // Wait for the first mount to complete loading
      const checkInterval = setInterval(() => {
        // Check for decrypted content first (for sensitive notes)
        if (globalDecryptedContentCache.has(decryptedCacheKey)) {
          console.log('🔍 EditNotePage: Decrypted content available from first instance, using it')
          const decryptedData = globalDecryptedContentCache.get(decryptedCacheKey)
          setNoteData(decryptedData)
          
          // Reset form with decrypted data
          const formDataToUse = hasSavedChanges ? savedFormData : {
            title: decryptedData.title || '',
            content: decryptedData.content || ''
          }
          
          // Only reset if the form data is different from current values
          const currentTitle = watch('title') || ''
          const currentContent = watch('content') || ''
          
          if (currentTitle !== formDataToUse.title || currentContent !== formDataToUse.content) {
            // Reset form with appropriate data
            setTimeout(() => {
              reset(formDataToUse)
            }, 100)
          }
          
          // Initialize tab's formData if user hasn't made changes yet
          if (isInTab && tabId && !hasSavedChanges) {
            updateTab(tabId, { 
              formData: formDataToUse,
              title: `Edit: ${decryptedData.title || 'Untitled Note'}`
            })
          } else if (isInTab && tabId && decryptedData.title) {
            updateTab(tabId, { title: `Edit: ${decryptedData.title}` })
          }
          
          setIsPageLoading(false)
          clearInterval(checkInterval)
        } else if (globalNoteDataCache.has(noteDataKey)) {
          console.log('🔍 EditNotePage: Note loaded by first instance, using cached data')
          const cachedNoteData = globalNoteDataCache.get(noteDataKey)
          setNoteData(cachedNoteData)
          
          // Reset form with cached data
          const formDataToUse = hasSavedChanges ? savedFormData : {
            title: cachedNoteData.title || '',
            content: cachedNoteData.content || ''
          }
          
          // Only reset if the form data is different from current values
          const currentTitle = watch('title') || ''
          const currentContent = watch('content') || ''
          
          if (currentTitle !== formDataToUse.title || currentContent !== formDataToUse.content) {
            // Reset form with appropriate data
            setTimeout(() => {
              reset(formDataToUse)
            }, 100)
          }
          
          // Initialize tab's formData if user hasn't made changes yet
          if (isInTab && tabId && !hasSavedChanges) {
            updateTab(tabId, { 
              formData: formDataToUse,
              title: `Edit: ${cachedNoteData.title || 'Untitled Note'}`
            })
          } else if (isInTab && tabId && cachedNoteData.title) {
            updateTab(tabId, { title: `Edit: ${cachedNoteData.title}` })
          }
          
          setIsPageLoading(false)
          clearInterval(checkInterval)
        } else if (!globalComponentMountCache.has(mountKey)) {
          console.log('🔍 EditNotePage: First instance finished, loading ourselves')
          clearInterval(checkInterval)
          loadNote(noteId)
        }
      }, 100)
      
      // Clear interval after 5 seconds to prevent infinite waiting
      setTimeout(() => {
        clearInterval(checkInterval)
        if (!globalNoteDataCache.has(noteDataKey) && !globalDecryptedContentCache.has(decryptedCacheKey)) {
          console.log('🔍 EditNotePage: Timeout waiting for first instance, loading ourselves')
          loadNote(noteId)
        }
      }, 5000)
      
      return
    }
    
    // Mark this note as being processed
    globalComponentMountCache.set(mountKey, { timestamp: Date.now() })
    
    // Resolve temp ID to final noteId if mapping exists
    let resolvedId = noteId
    try {
      if (noteId && noteId.startsWith('temp-')) {
        const raw = localStorage.getItem('tempToPermMap') || '{}'
        const map = JSON.parse(raw)
        if (map[noteId]) {
          resolvedId = map[noteId]
          console.log('🔄 EditNotePage: Resolved temp ID to final ID:', resolvedId)
        }
      }
    } catch (_) {}
    
    if (resolvedId && resolvedId !== 'undefined') {
      loadNote(resolvedId)
    } else {
      console.error('❌ EditNotePage: Invalid note ID:', noteId)
      toast.error('Invalid note ID')
      if (isInTab && tabId) {
        closeTab(tabId)
      } else {
        navigate('/notes')
      }
    }
    
    // Cleanup on unmount
    return () => {
      globalComponentMountCache.delete(mountKey)
      // Don't delete noteData from cache as other instances might need it
    }
  }, [noteId, isInTab, tabId, navigate])

  // Listen for decryption complete events from other component instances
  useEffect(() => {
    const handleDecryptionComplete = (event) => {
      const { noteId: eventNoteId, decryptedData } = event.detail
      const currentNoteId = noteId.startsWith('note-') ? noteId : `note-${noteId}`
      
      if (eventNoteId === currentNoteId && !isNoteDecrypted) {
        console.log('🔐 EditNotePage: Received decryption complete event for current note, updating form')
        
        // Update the note data with decrypted content
        setNoteData(decryptedData)
        setIsNoteDecrypted(true)
        
        // Reset form with decrypted content
        const formDataToUse = {
          title: decryptedData.title || '',
          content: decryptedData.content || ''
        }
        
        console.log('🔐 EditNotePage: Resetting form with decrypted content:', {
          title: formDataToUse.title,
          content: formDataToUse.content,
          contentLength: formDataToUse.content?.length
        })
        
        // Add a small delay to ensure RichTextEditor is fully initialized
        setTimeout(() => {
          reset(formDataToUse)
        }, 100)
        
        // Initialize tab's formData if user hasn't made changes yet
        if (isInTab && tabId && !hasSavedChanges) {
          updateTab(tabId, { 
            formData: formDataToUse,
            title: `Edit: ${decryptedData.title || 'Untitled Note'}`
          })
        } else if (isInTab && tabId && decryptedData.title) {
          updateTab(tabId, { title: `Edit: ${decryptedData.title}` })
        }
      }
    }

    window.addEventListener('sensitiveNoteDecrypted', handleDecryptionComplete)
    
    return () => {
      window.removeEventListener('sensitiveNoteDecrypted', handleDecryptionComplete)
    }
  }, [noteId, isInTab, tabId, hasSavedChanges, updateTab, reset, isNoteDecrypted])

  /**
   * Load note data from API
   */
  const loadNote = async (resolvedIdParam = null) => {
    try {
      const idToLoad = resolvedIdParam || noteId
      
      // Check global cache to prevent duplicate prompts across component instances
      const globalPromptKey = `password-prompt-${idToLoad}`
      const decryptedCacheKey = `decrypted-${idToLoad}`
      const decryptedContent = globalProcessedAuthCache.get(decryptedCacheKey)
      
      console.log('🔐 EditNotePage: Checking for decrypted content:', {
        globalPromptKey,
        decryptedCacheKey,
        hasDecryptedContent: !!decryptedContent,
        hasDecryptedData: !!(decryptedContent && decryptedContent.decryptedData),
        passwordPromptInProgress: globalPasswordPromptCache.has(globalPromptKey)
      })
      
      // If we have decrypted content, use it even if password prompt is in progress
      if (decryptedContent && decryptedContent.decryptedData) {
        console.log('🔐 EditNotePage: Found cached decrypted content, using it instead of prompting for password')
        
        // Set the decrypted note data
        setNoteData(decryptedContent.decryptedData)
        setIsNoteDecrypted(true)
        
        // Reset form with decrypted content
        const formDataToUse = {
          title: decryptedContent.decryptedData.title || '',
          content: decryptedContent.decryptedData.content || ''
        }
        reset(formDataToUse)
        
        // Initialize tab's formData if user hasn't made changes yet
        if (isInTab && tabId && !hasSavedChanges) {
          updateTab(tabId, { 
            formData: formDataToUse,
            title: `Edit: ${decryptedContent.decryptedData.title || 'Untitled Note'}`
          })
        } else if (isInTab && tabId && decryptedContent.decryptedData.title) {
          updateTab(tabId, { title: `Edit: ${decryptedContent.decryptedData.title}` })
        }
        
        // Clear the global cache flag since we're using cached content
        globalPasswordPromptCache.delete(globalPromptKey)
        return
      }
      
      if (globalPasswordPromptCache.has(globalPromptKey)) {
        console.log('🔐 EditNotePage: Password prompt already in progress globally for this note, skipping duplicate')
        console.log('🔐 EditNotePage: But let me check if we have decrypted content anyway...')
        
        // Even if password prompt is in progress, check if we have decrypted content
        if (decryptedContent && decryptedContent.decryptedData) {
          console.log('🔐 EditNotePage: Found decrypted content even though password prompt is in progress, using it!')
          
          // Set the decrypted note data
          setNoteData(decryptedContent.decryptedData)
          setIsNoteDecrypted(true)
          
          // Reset form with decrypted content
          const formDataToUse = {
            title: decryptedContent.decryptedData.title || '',
            content: decryptedContent.decryptedData.content || ''
          }
          reset(formDataToUse)
          
          // Initialize tab's formData if user hasn't made changes yet
          if (isInTab && tabId && !hasSavedChanges) {
            updateTab(tabId, { 
              formData: formDataToUse,
              title: `Edit: ${decryptedContent.decryptedData.title || 'Untitled Note'}`
            })
          } else if (isInTab && tabId && decryptedContent.decryptedData.title) {
            updateTab(tabId, { title: `Edit: ${decryptedContent.decryptedData.title}` })
          }
          
          // Clear the global cache flag since we're using cached content
          globalPasswordPromptCache.delete(globalPromptKey)
          setIsPageLoading(false)
          return
        }
        
        // If no decrypted content and password prompt is in progress, don't set loading to false
        // Let the first mount that's actually loading the note handle the loading state
        console.log('🔐 EditNotePage: Password prompt in progress but no decrypted content, waiting for first mount to complete')
        return
      }
      
      // Set a temporary flag immediately to prevent race conditions
      globalPasswordPromptCache.set(globalPromptKey, { inProgress: true, timestamp: Date.now() })
      
      // Prevent duplicate loading of the same note
      if (loadingNotesRef.current.has(idToLoad)) {
        console.log('🔍 EditNotePage: Note already loading, skipping duplicate call for ID:', idToLoad)
        // Don't set loading to false here, let the first instance handle it
        return
      }
      
      console.log('🔍 EditNotePage: Loading note with ID:', idToLoad)
      loadingNotesRef.current.add(idToLoad)
      setIsPageLoading(true)
      
      // Clean up expired cache entries
      cleanupExpiredCache()
      
      let response
      
      // If in local mode, get note from local storage
      if (isLocalMode) {
        console.log('🔍 EditNotePage: Loading note from local storage')
        response = await getLocalNote(idToLoad)
        console.log('✅ EditNotePage: Note loaded from local storage:', response)
        
        if (!response) {
          console.error('❌ EditNotePage: Note not found in local storage:', idToLoad)
          toast.error('Note not found')
          if (isInTab && tabId) {
            closeTab(tabId)
          } else {
            navigate('/notes')
          }
          return
        }
        

      } else {
        // Cloud mode
        if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
          // Try cache first (FrontendSync IndexedDB)
          let cached = await frontendSync.getCachedNoteById(idToLoad)
          if (!cached) {
            // Load from Drive then recheck cache
            await frontendSync.loadNotes({ forceRefresh: true, pageSize: 100 })
            cached = await frontendSync.getCachedNoteById(idToLoad)
          }
          response = cached
          console.log('✅ EditNotePage: Note loaded via frontend sync:', response)
        } else {
          // Backend fallback
          response = await notesAPI.getNoteById(idToLoad)
          console.log('✅ EditNotePage: Note loaded from API:', response)
        }
      }
      
      // Check if this is an encrypted sensitive note (for both local and cloud modes)
      // Check both the isEncrypted flag and if content appears encrypted
      const isEncrypted = response?.isEncrypted || (response?.content && isContentEncrypted(response.content))
      const cacheKey = `${idToLoad}-${response.content?.length || 0}`
      const cacheEntry = globalProcessedAuthCache.get(cacheKey)
      const isCacheValid = cacheEntry && (Date.now() - cacheEntry.timestamp) < 300000 // 5 minutes expiration
      const isUserCurrentlyAuthenticated = sensitiveSession.isAuthenticated
      
      // For sensitive notes, check if we've already processed this note recently
      const hasProcessedThisNote = isCacheValid && cacheEntry?.decrypted
      
      console.log('🔍 EditNotePage: Checking sensitive note detection:', {
        isEncrypted,
        contentLength: response.content?.length,
        cacheKey,
        cacheEntry,
        isCacheValid,
        isUserCurrentlyAuthenticated,
        hasProcessedThisNote,
        noteId: idToLoad
      })
      
      if (response && isEncrypted && response.content && !hasProcessedThisNote) {
        console.log('🔐 EditNotePage: Note is encrypted, checking if user is authenticated', {
          isEncrypted: response.isEncrypted,
          contentAppearsEncrypted: isContentEncrypted(response.content),
          contentLength: response.content?.length,
          hasProcessedThisNote,
          noteId: idToLoad,
          cacheKey
        })
        
        // Check if we already have a pending note for this ID to prevent duplicate prompts
        if (pendingNote && (pendingNote.noteId === idToLoad || pendingNote.id === idToLoad)) {
          console.log('🔐 EditNotePage: Password prompt already in progress for this note, skipping duplicate')
          return
        }
        
        // Check if password prompt is already in progress
        if (passwordPromptRef.current) {
          console.log('🔐 EditNotePage: Password prompt already in progress, skipping duplicate')
          return
        }
        
        // For encrypted notes, always prompt for password for authentication
        console.log('🔐 EditNotePage: Note is encrypted, prompting for password for authentication')
        
        // Store the encrypted note and prompt for password
        setPendingNote(response)
        setIsPageLoading(false)
        passwordPromptRef.current = true
        globalProcessedAuthCache.set(cacheKey, { modalShown: true, timestamp: Date.now() }) // Prevent re-triggering for this note
        console.log('🔐 EditNotePage: Prompting for password - pendingNote: true')
        // Prompt for password using browser popup
        promptForPassword(response)
        return
      }
      
      if (response) {
        // Store note data in global cache for other component instances
        const noteDataKey = `noteData-${idToLoad}`
        globalNoteDataCache.set(noteDataKey, response)
        
        setNoteData(response)
        
        // Use saved form data only if it has actual content (user has made changes), otherwise use loaded note data
        const formDataToUse = hasSavedChanges ? savedFormData : {
          title: response.title || '',
          content: response.content || ''
        }
        
        // Reset form with appropriate data
        // Add a small delay to ensure RichTextEditor is properly initialized
        setTimeout(() => {
          reset(formDataToUse)
        }, 100)
        
        // Initialize tab's formData if user hasn't made changes yet
        if (isInTab && tabId && !hasSavedChanges) {
          updateTab(tabId, { 
            formData: formDataToUse,
            title: `Edit: ${response.title || 'Untitled Note'}`
          })
        } else if (isInTab && tabId && response.title) {
          updateTab(tabId, { title: `Edit: ${response.title}` })
        }
        
        // Clear the global cache flag since this note doesn't need password prompt
        globalPasswordPromptCache.delete(globalPromptKey)
        
        // Set loading to false after noteData is set
        setIsPageLoading(false)
      } else {
        setIsPageLoading(false)
      }
    } catch (error) {
      console.error('❌ EditNotePage: Error loading note:', error)
      
      // Clear the global cache flag on error
      globalPasswordPromptCache.delete(globalPromptKey)
      
      // Set loading to false on error
      setIsPageLoading(false)
      
      if (error.response?.status === 404) {
        toast.error('Note not found')
        if (isInTab && tabId) {
          closeTab(tabId)
        } else {
          navigate('/notes')
        }
      } else if (error.response?.status === 401) {
        // Only redirect to login if not in local mode
        if (isLocalMode) {
          setError('root', { message: 'Failed to load note from local storage' })
        } else {
          toast.error('Please log in to edit notes')
          navigate('/login')
        }
      } else {
        toast.error('Failed to load note')
        if (isInTab && tabId) {
          closeTab(tabId)
        } else {
          navigate('/notes')
        }
      }
    } finally {
      // Clean up loading ref
      if (resolvedIdParam) {
        loadingNotesRef.current.delete(resolvedIdParam)
      } else {
        loadingNotesRef.current.delete(noteId)
      }
    }
  }

  /**
   * Handle form submission to update the note
   */
  const onSubmit = async (data) => {
    console.log('🔍 EditNotePage: onSubmit called with data:', {
      title: data.title,
      contentLength: data.content?.length,
      hasChanges: hasChanges()
    })
    
    try {
      setIsLoading(true)
      clearErrors()

      // Validate that at least one field has content
      if (!data.title?.trim() && !data.content?.trim()) {
        setError('root', { message: 'Please provide either a title or content for your note.' })
        setIsLoading(false)
        return
      }

      // Process content to replace base64 images with Google Drive URLs
      let processedContent = data.content
      if (richTextEditorRef.current && richTextEditorRef.current.processContentForSave) {
        processedContent = richTextEditorRef.current.processContentForSave(data.content)
        console.log('🔄 EditNotePage: Processed content for save:', processedContent !== data.content ? 'YES' : 'NO')
      }

      // Check if this is a sensitive note and encrypt the content before saving
      let dataToSave = { ...data, content: processedContent }
      if (noteData && noteData.folderId) {
        const noteFolder = folders.find(f => f.folderId === noteData.folderId)
        if (noteFolder?.folderType === 'SENSITIVE' && sensitiveSession.isAuthenticated) {
          console.log('🔐 EditNotePage: Encrypting sensitive note content before saving')
          try {
            // Try to get password from LocalModeContext first, then from SensitiveSessionContext
            let password = getSensitivePassword()
            
            console.log('🔐 EditNotePage: Password check for saving:', {
              hasPassword: !!password,
              passwordLength: password?.length,
              isAuthenticated: sensitiveSession.isAuthenticated,
              sessionStoragePassword: !!sessionStorage.getItem('localSensitivePassword')
            })
            
            // If no password in LocalModeContext, check if we have a recent authentication
            if (!password && sensitiveSession.isAuthenticated) {
              console.log('🔐 EditNotePage: No local password, but user is authenticated - prompting for password')
              // Prompt for password again to get the password for saving
              setPendingNote({ ...noteData, content: data.content })
              setIsPasswordForSave(true)
              promptForPassword({ ...noteData, content: data.content })
              setIsLoading(false)
              return
            }
            
            if (password && data.content) {
              // Only encrypt the content, keep title unencrypted for display
              const { encryptText } = await import('../../utils/crypto')
              const encryptedContent = await encryptText(data.content, password)
              dataToSave = { 
                ...data, 
                content: encryptedContent, 
                isEncrypted: true 
              }
              console.log('✅ EditNotePage: Note content encrypted successfully with crypto.js')
            } else {
              console.error('❌ EditNotePage: No sensitive password available for encryption')
              toast.error('Sensitive password not available. Please re-authenticate to save.')
              // Prompt for password again to get the password for saving
              setPendingNote({ ...noteData, content: data.content })
              setIsPasswordForSave(true)
              promptForPassword({ ...noteData, content: data.content })
              setIsLoading(false)
              return
            }
          } catch (error) {
            console.error('❌ EditNotePage: Failed to encrypt note content:', error)
            toast.error('Failed to encrypt note content. Please try again.')
            setIsLoading(false)
            return
          }
        }
      }

      // Use unified updateNote from NotesContext (handles both local and cloud modes)
      console.log('🔍 EditNotePage: Updating note data:', dataToSave)
      const response = await updateNote(noteId, dataToSave)
      console.log('✅ EditNotePage: Note updated successfully:', response)
      
      // Reset loading state
      console.log('🔍 EditNotePage: Resetting loading state after successful save')
      setIsLoading(false)
      
      // For cloud mode, show immediate success since optimistic update happened
      try {
        if (!isLocalMode) {
          console.log('🔍 EditNotePage: Showing success message for cloud mode')
          showSuccess('✅ Note saved! Syncing to cloud in background...')
          console.log('🔍 EditNotePage: Success message shown for cloud mode')
        } else {
          console.log('🔍 EditNotePage: Showing success message for local mode')
          showSuccess('✅ Note saved successfully!')
          console.log('🔍 EditNotePage: Success message shown for local mode')
        }
      } catch (successError) {
        console.error('❌ EditNotePage: Error showing success message:', successError)
      }

      // Refresh notes list and folder counts (for cloud mode) - non-blocking
      if (!isLocalMode) {
        console.log('🔍 EditNotePage: Refreshing notes and folders for cloud mode (non-blocking)')
        // Start refresh operations but don't wait for them
        Promise.all([
          fetchNotes(),
          fetchFolders() // Update folder note counts
        ]).then(() => {
          console.log('🔍 EditNotePage: Notes and folders refreshed successfully')
        }).catch((refreshError) => {
          console.error('❌ EditNotePage: Error refreshing notes/folders:', refreshError)
        })
      }

      // Update tab title if in tab mode
      if (isInTab && tabId && data.title) {
        updateTab(tabId, { 
          title: `Edit: ${data.title}`,
          isDirty: false 
        })
      }

      // Success message is handled by the updateNote function
      
      console.log('🔍 EditNotePage: About to navigate - checking conditions:', {
        isInTab,
        tabId,
        hasTabId: !!tabId,
        response: !!response
      })
      
      try {
        if (isInTab && tabId) {
          // Stay in tab but open view mode
          console.log('🔍 EditNotePage: Opening view tab and closing edit tab')
          openTab({
            type: 'view',
            noteId: noteId,
            title: data.title || 'Untitled Note',
            note: response
          })
          closeTab(tabId)
        } else {
          console.log('🔍 EditNotePage: Navigating back to notes list')
          navigate('/notes')
        }
        console.log('🔍 EditNotePage: Navigation completed successfully')
      } catch (navError) {
        console.error('❌ EditNotePage: Error during navigation:', navError)
        // Even if navigation fails, we should still reset loading state
        setIsLoading(false)
      }
    } catch (error) {
      console.error('❌ EditNotePage: Error updating note:', error)
      console.error('❌ EditNotePage: Error details:', {
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      })
      
      // In local mode, we don't expect authentication errors
      if (isLocalMode) {
        setError('root', { message: 'Failed to update note locally. Please try again.' })
        setIsLoading(false)
        return
      }
      
      // Handle specific error types for cloud mode
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        showError('⏱️ Save timeout - Google Drive has corrupted files causing sync delays. Note saved locally.')
        setError('root', { 
          message: 'Note saved locally but Google Drive sync timed out due to corrupted files. Your changes are safe. Consider manually cleaning your Google Drive folder.' 
        })
      } else if (error.response?.status === 404 || 
                 error.response?.data?.message?.includes('Note file not found in cloud storage') ||
                 error.message?.includes('Note file not found in cloud storage') ||
                 (error.response?.status >= 400 && error.response?.status < 500 && 
                  error.response?.data?.message?.includes('Failed to update note'))) {
        // File not found in cloud - but check if it was saved locally as fallback
        showWarning('📂 Note file not found in Google Drive - saved to local storage instead.')
        setError('root', { message: 'Note saved locally. Google Drive file was missing but your changes are safe.' })
        // Still proceed with the UI updates since the note was actually saved locally
        setIsLoading(false)
        return // Don't throw error - treat as success with warning
      } else if (error.message?.includes('Google Drive connection expired')) {
        showWarning('🔄 Google Drive connection expired. Please reconnect to sync changes.')
        setError('root', { message: 'Google Drive connection expired. Note saved locally - please reconnect to sync.' })
      } else if (error.response?.status === 400) {
        const errorData = error.response.data
        if (errorData.field) {
          setError(errorData.field, { message: errorData.message })
        } else {
          setError('root', { message: errorData.message || 'Failed to update note. Please check your input.' })
        }
        showError('❌ Failed to save note - please check your input')
      } else if (error.response?.status === 401) {
        showError('🔐 Please log in to edit notes')
        navigate('/login')
      } else if (error.response?.status === 404) {
        showError('❌ Note not found')
        navigate('/notes')
      } else if (error.response?.status >= 500) {
        showError('⚠️ Server error - note saved locally, will sync when server recovers')
        setError('root', { message: 'Server temporarily unavailable. Note saved locally and will sync automatically.' })
      } else {
        showError('❌ Failed to save note - please try again')
        setError('root', { message: 'Failed to update note. Please try again.' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Listen for mobile save trigger from header
  useEffect(() => {
    const handleMobileSave = () => {
      handleSubmit(onSubmit)()
    }

    window.addEventListener('mobile-save-trigger', handleMobileSave)
    return () => window.removeEventListener('mobile-save-trigger', handleMobileSave)
  }, [handleSubmit, onSubmit])

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'ctrl+shift+s': (event) => {
      console.log('🎹 Keyboard shortcut: Ctrl+Shift+S - Saving note')
      handleSubmit(onSubmit)()
      // Status message will be shown by onSubmit function
    }
  }, [handleSubmit, onSubmit])

  /**
   * Handle Tab key navigation from title to content
   */
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault()
      // Focus the content editor
      if (contentEditorRef.current) {
        // For RichTextEditor, we need to focus the internal editor
        const editorElement = contentEditorRef.current.querySelector('[contenteditable="true"], textarea, .ql-editor')
        if (editorElement) {
          editorElement.focus()
        }
      }
    }
  }

  /**
   * Handle saving a sensitive note with the provided password
   */
  const handleSaveWithPassword = async (formData, password) => {
    try {
      console.log('🔐 EditNotePage: Saving sensitive note with password...')
      
      // Process content to replace base64 images with Google Drive URLs
      let processedContent = formData.content
      if (richTextEditorRef.current && richTextEditorRef.current.processContentForSave) {
        processedContent = richTextEditorRef.current.processContentForSave(formData.content)
        console.log('🔄 EditNotePage: Processed content for save (with password):', processedContent !== formData.content ? 'YES' : 'NO')
      }

      // Encrypt the content before saving
      let dataToSave = { ...formData, content: processedContent }
      if (password && formData.content) {
        const encryptedContent = localStorageService.encryptContent(formData.content, password)
        dataToSave = { ...formData, content: encryptedContent, isEncrypted: true }
        console.log('✅ EditNotePage: Note content encrypted successfully for saving')
      }
      
      // Use unified updateNote from NotesContext (handles both local and cloud modes)
      console.log('🔍 EditNotePage: Updating note data:', dataToSave)
      const response = await updateNote(noteId, dataToSave)
      console.log('✅ EditNotePage: Note updated successfully:', response)
      
      // For cloud mode, show immediate success since optimistic update happened
      if (!isLocalMode) {
        showSuccess('✅ Note saved! Syncing to cloud in background...')
      } else {
        showSuccess('✅ Note saved successfully!')
      }
      
      // Refresh notes list and folder counts (for cloud mode)
      if (!isLocalMode) {
        await Promise.all([
          fetchNotes(),
          fetchFolders() // Update folder note counts
        ])
      }
      
      // Update tab title if in tab mode
      if (isInTab && tabId && formData.title) {
        updateTab(tabId, { 
          title: `Edit: ${formData.title}`,
          isDirty: false 
        })
      }
      
      if (isInTab && tabId) {
        // Stay in tab but open view mode
        openTab({
          type: 'view',
          noteId: noteId,
          title: formData.title || 'Untitled Note',
          note: response
        })
        closeTab(tabId)
      } else {
        navigate('/notes')
      }
      
    } catch (error) {
      console.error('❌ EditNotePage: Error saving sensitive note:', error)
      toast.error('Failed to save note. Please try again.')
      throw error
    }
  }

  /**
   * Prompt for password using browser popup
   */
  const promptForPassword = async (noteToDecrypt) => {
    const noteId = noteToDecrypt.noteId || noteToDecrypt.id
    const cacheKey = `password-prompt-${noteId}-${isPasswordForSave ? 'save' : 'access'}`
    
    // Check if password prompt is already in progress for this note
    if (globalPasswordPromptCache.has(cacheKey)) {
      console.log('🔐 EditNotePage: Password prompt already in progress for this note, skipping duplicate')
      return
    }
    
    // Mark password prompt as in progress
    globalPasswordPromptCache.set(cacheKey, { inProgress: true, timestamp: Date.now() })
    
    try {
      const noteTitle = noteToDecrypt.title || 'this note'
      const isForSave = isPasswordForSave
      const action = isForSave ? 'save' : 'access'
      
      const password = window.prompt(
        `This note "${noteTitle}" is encrypted and requires your password to ${action}.\n\nEnter your password:`
      )
      
      if (password === null) {
        // User cancelled
        console.log('🔐 EditNotePage: User cancelled password prompt')
        globalPasswordPromptCache.delete(cacheKey)
        handlePasswordCancel()
        return
      }
      
      if (!password.trim()) {
        // Empty password
        toast.error('Password is required')
        // Retry the prompt
        setTimeout(() => {
          globalPasswordPromptCache.delete(cacheKey)
          promptForPassword(noteToDecrypt)
        }, 100)
        return
      }
      
      try {
        await handlePasswordConfirm(password, noteToDecrypt)
        // Clear the cache on successful authentication
        globalPasswordPromptCache.delete(cacheKey)
      } catch (error) {
        console.error('❌ EditNotePage: Password authentication failed:', error)
        toast.error(error.message || 'Invalid password. Please try again.')
        // Retry the prompt
        setTimeout(() => {
          globalPasswordPromptCache.delete(cacheKey)
          promptForPassword(noteToDecrypt)
        }, 100)
      }
    } catch (error) {
      // Clear the cache on any error
      globalPasswordPromptCache.delete(cacheKey)
      throw error
    }
  }

  /**
   * Handle password confirmation for sensitive note access
   */
  const handlePasswordConfirm = async (password, noteToDecrypt = null) => {
    console.log('🔐 EditNotePage: Password confirmation handler called with password length:', password?.length)
    try {
      if (isPasswordForSave) {
        // This is for saving - we need to encrypt the current form content
        console.log('🔐 EditNotePage: Password provided for saving, proceeding with save...')
        
        // Store the password in session for future use
        const sessionToken = `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const sessionExpiry = new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutes
        sensitiveSession.authenticate(sessionToken, sessionExpiry, password)
        
        // Also store the password in LocalModeContext for future encryption operations
        try {
          setSensitivePassword(password)
          console.log('🔐 EditNotePage: Password stored in LocalModeContext for future use')
        } catch (error) {
          console.warn('⚠️ EditNotePage: Could not store password in LocalModeContext:', error)
        }
        
        // Clear pending note and reset flags
        setPendingNote(null)
        setIsPasswordForSave(false)
        passwordPromptRef.current = false
        
        // Clear global password prompt cache
        const globalPromptKey = `password-prompt-${noteId}`
        globalPasswordPromptCache.delete(globalPromptKey)
        
        // Now proceed with the save operation
        const formData = { title: watchedTitle || '', content: watchedContent || '' }
        await handleSaveWithPassword(formData, password)
        
        return { success: true }
      } else {
        // This is for viewing - decrypt the note content
        const noteData = noteToDecrypt || pendingNote
        if (noteData && noteData.content) {
          console.log('🔐 EditNotePage: Attempting to decrypt note content with password...')
          
          try {
            // Use the same decryption method as SensitiveNotesService
            const { decryptText } = await import('../../utils/crypto')
            const decryptedContent = await decryptText(noteData.content, password)
            console.log('✅ EditNotePage: Note decrypted successfully, content length:', decryptedContent?.length)
            
            // Check if decryption was successful by looking for gibberish
            // If the content is still encrypted-looking, the password was wrong
            if (isContentEncrypted(decryptedContent)) {
              console.log('❌ EditNotePage: Decryption failed - content still appears encrypted')
              throw new Error('Invalid password. Please try again.')
            }
            
            // Update the note with decrypted content and set it as the current note data
            const decryptedNote = { ...noteData, content: decryptedContent, isEncrypted: false }
            setNoteData(decryptedNote)
            setIsNoteDecrypted(true)
            
            // Store decrypted content in global cache for other component instances
            const decryptedCacheKey = `decrypted-${noteData.noteId || noteData.id}`
            globalProcessedAuthCache.set(decryptedCacheKey, { 
              decryptedData: decryptedNote, 
              timestamp: Date.now() 
            })
            
            console.log('🔐 EditNotePage: Stored decrypted content in global cache:', {
              decryptedCacheKey,
              decryptedData: decryptedNote,
              contentLength: decryptedNote.content?.length
            })
            
            // Dispatch a custom event to notify other component instances
            const decryptionCompleteEvent = new CustomEvent('sensitiveNoteDecrypted', {
              detail: {
                noteId: noteData.noteId || noteData.id,
                decryptedData: decryptedNote
              }
            })
            window.dispatchEvent(decryptionCompleteEvent)
            
            // Update the form fields with the decrypted content
            setValue('title', decryptedNote.title || '')
            setValue('content', decryptedContent || '')
            
            // Reset the form with the decrypted content to ensure RichTextEditor gets the proper content
            reset({
              title: decryptedNote.title || '',
              content: decryptedContent || ''
            })
            
            console.log('🔐 EditNotePage: Note state updated, modal closed')
            
            // Mark this note as processed to prevent re-triggering
            const pendingCacheKey = `${noteData.noteId || noteData.id}-${noteData.content?.length || 0}`
            globalProcessedAuthCache.set(pendingCacheKey, { decrypted: true, timestamp: Date.now() })
            
            // Clear pending note and reset password prompt ref
            setPendingNote(null)
            passwordPromptRef.current = false
            
            // Clear global password prompt cache
            const globalPromptKey = `password-prompt-${noteData.noteId || noteData.id}`
            globalPasswordPromptCache.delete(globalPromptKey)
            
            // Store the password in session for future use (without backend authentication)
            // Create a simple session token for frontend use
            const sessionToken = `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            const sessionExpiry = new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutes
            sensitiveSession.authenticate(sessionToken, sessionExpiry, password)
            
            // Also store the password in LocalModeContext for future encryption operations
            try {
              // Use the setSensitivePassword function if available
              if (typeof setSensitivePassword === 'function') {
                setSensitivePassword(password)
              } else {
                // Fallback to sessionStorage
                sessionStorage.setItem('localSensitivePassword', password)
              }
              console.log('🔐 EditNotePage: Password stored in LocalModeContext for future use')
            } catch (error) {
              console.warn('⚠️ EditNotePage: Could not store password in LocalModeContext:', error)
            }
            
            // Clear the cache entry since authentication succeeded
            globalProcessedAuthCache.delete(pendingCacheKey)
            
            // Return success to indicate the operation completed
            return { success: true }
            
          } catch (error) {
            console.error('❌ EditNotePage: Failed to decrypt note:', error)
            throw error // Let the modal handle the error display
          }
        }
      }
      
      // Return success if no pending note (shouldn't happen)
      return { success: true }
    } catch (error) {
      console.error('❌ EditNotePage: Password decryption failed:', error)
      throw error // Let the modal handle the error display
    }
  }

  /**
   * Handle password cancellation
   */
  const handlePasswordCancel = () => {
    console.log('🔐 EditNotePage: Password cancelled')
    setPendingNote(null)
    setIsPasswordForSave(false)
    passwordPromptRef.current = false
    
    // Clear global password prompt cache
    const globalPromptKey = `password-prompt-${noteId}`
    globalPasswordPromptCache.delete(globalPromptKey)
    
    // Navigate back to notes page if not in tab mode
    if (!isInTab) {
      navigate('/notes')
    } else if (tabId) {
      closeTab(tabId)
    }
  }

  /**
   * Handle note deletion
   */
  const handleDelete = async () => {
    if (!noteData) return

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${noteData.title || 'this note'}"? This action cannot be undone.`
    )
    
    if (!confirmDelete) return

    try {
      setIsDeleting(true)
      console.log('🔍 EditNotePage: Deleting note:', noteId)
      
      await deleteNote(noteId)
      // Refresh folder data to update note counts
      await fetchFolders()
      
      if (isInTab && tabId) {
        closeTab(tabId)
      } else {
        navigate('/notes', { replace: true })
      }
    } catch (error) {
      console.error('❌ EditNotePage: Error deleting note:', error)
      
      if (error.response?.status === 401) {
        toast.error('Please log in to delete notes')
        navigate('/login')
      } else if (error.response?.status === 404) {
        toast.error('Note not found')
      } else {
        toast.error('Failed to delete note. Please try again.')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  /**
   * Handle expand/restore toggle
   */
  const handleExpandToggle = (e) => {
    e.preventDefault() // Prevent form submission
    e.stopPropagation() // Stop event bubbling
    setIsExpanded(!isExpanded)
  }

  /**
   * Handle navigation away with unsaved changes
   */
  const handleNavigateAway = (e) => {
    if (hasChanges() && !isLoading) {
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
      )
      if (!confirmLeave) {
        e.preventDefault()
        return false
      }
    }
    return true
  }

  // Add beforeunload event listener for browser navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges() && !isLoading) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [watchedTitle, watchedContent, noteData, isLoading])

  // Cleanup effect to reset password prompt ref when component unmounts
  useEffect(() => {
    return () => {
      passwordPromptRef.current = false
      // Clear the global password prompt cache on unmount
      const globalPromptKey = `password-prompt-${noteId}`
      globalPasswordPromptCache.delete(globalPromptKey)
      
      // Clear note data from global cache after a delay to allow other instances to use it
      setTimeout(() => {
        const noteDataKey = `noteData-${noteId}`
        globalNoteDataCache.delete(noteDataKey)
      }, 1000)
    }
  }, [noteId])

  // Show loading spinner while loading note data
  if (isPageLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Debug modal state (commented out to reduce console spam)
  // console.log('🔍 EditNotePage: Modal state:', { showPasswordModal, pendingNote: !!pendingNote })

  // Show error if note data couldn't be loaded (but allow modal to show if we have pendingNote)
  if (!noteData && !pendingNote) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Note not found</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          The note you're trying to edit doesn't exist or has been deleted.
        </p>
        <Link to="/notes" className="btn-primary">
          Back to Notes
        </Link>
      </div>
    )
  }

  // If we have a pending note (waiting for password), show a loading state
  if (pendingNote && !noteData) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center min-h-64">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading note...</p>
          </div>
        </div>
        
        {/* Password prompt is now handled via browser popup */}
      </div>
    )
  }

  return (
    <div className="space-y-2">

      {/* Edit Note Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className={`card ${isExpanded ? 'note-fullscreen' : ''}`}>
          <div className="card-body space-y-2 relative">
            {isExpanded ? (
              /* NEW: Floating Controls Fullscreen Layout */
              <>
                {/* Floating Action Controls */}
                <div className="floating-controls">
                  {/* Save Button */}
                  <button
                    type="submit"
                    disabled={isLoading || !hasChanges()}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 rounded-lg transition-colors shadow-lg"
                    title="Save changes"
                  >
                    {isLoading ? (
                      <ButtonSpinner size="sm" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                  </button>

                  {/* Cancel Button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (isInTab) {
                        closeTab(tabId)
                      } else {
                        navigate('/notes')
                      }
                    }}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors shadow-lg"
                    title="Cancel editing"
                  >
                    <X className="h-4 w-4" />
                  </button>

                  {/* Exit Fullscreen Button */}
                  <button
                    type="button"
                    onClick={handleExpandToggle}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors shadow-lg"
                    title="Exit fullscreen"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Floating Title Field */}
                <div className="floating-title">
                  <input
                    {...register('title', {
                      required: 'Note title is required',
                      maxLength: {
                        value: 255,
                        message: 'Title must be less than 255 characters'
                      }
                    })}
                    ref={(e) => {
                      register('title').ref(e) // react-hook-form ref
                      titleInputRef.current = e // our custom ref
                    }}
                    type="text"
                    id="title"
                    placeholder="Enter note title..."
                    disabled={isLoading}
                    onKeyDown={handleTitleKeyDown}
                  />
                </div>

                {/* Fullscreen Editor */}
                <div className="fullscreen-editor" ref={contentEditorRef}>
                  <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        ref={richTextEditorRef}
                        key={`content-fullscreen-${noteId}-${noteData?.lastModified || 'initial'}`}
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Start writing your note..."
                        disabled={isLoading}
                        className="rich-text-editor"
                        isFullscreen={true}
                      />
                    )}
                  />
                </div>

                {/* Error Messages (floating above bottom toolbar) */}
                {(errors.root || errors.title || errors.content) && (
                  <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-1100 max-w-md">
                    {errors.root && (
                      <div className="bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-2 backdrop-blur-lg">
                        <div className="text-sm text-red-700 dark:text-red-300">
                          {errors.root.message}
                        </div>
                      </div>
                    )}
                    {errors.title && (
                      <div className="bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-2 backdrop-blur-lg">
                        <div className="text-sm text-red-700 dark:text-red-300">
                          {errors.title.message}
                        </div>
                      </div>
                    )}
                    {errors.content && (
                      <div className="bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-800 rounded-lg p-3 backdrop-blur-lg">
                        <div className="text-sm text-red-700 dark:text-red-300">
                          {errors.content.message}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Normal Layout */
              <>
                {/* Expand/Restore Button */}
                <div className="expand-button">
                  {/* HIDDEN: Expand to full screen button - commented out for now
                  <button
                    type="button"
                    onClick={handleExpandToggle}
                    className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 shadow-sm transition-colors duration-200"
                    title="Expand to full screen"
                  >
                    <Maximize2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  */}
                </div>

                {/* Form Errors */}
                {errors.root && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="text-sm text-red-700 dark:text-red-300">
                      {errors.root.message}
                    </div>
                  </div>
                )}

                {/* Title Field with Action Buttons */}
                <div>
                  <div className="flex gap-3 items-start">
                    {/* Title Input - 60% width */}
                    <div className="flex-1" style={{ width: '60%' }}>
                      <input
                        {...register('title', {
                          required: 'Note title is required',
                          maxLength: {
                            value: 255,
                            message: 'Title must be less than 255 characters'
                          }
                        })}
                        ref={(e) => {
                          register('title').ref(e) // react-hook-form ref
                          titleInputRef.current = e // our custom ref
                        }}
                        type="text"
                        id="title"
                        className={`form-input ${errors.title ? 'error' : ''}`}
                        placeholder="Enter note title..."
                        disabled={isLoading}
                        onKeyDown={handleTitleKeyDown}
                      />
                      {errors.title && (
                        <p className="form-error">{errors.title.message}</p>
                      )}
                      <p className="form-help">
                        {watchedTitle?.length || 0}/255 characters
                      </p>
                    </div>
                    
                    {/* Action Buttons - 40% width divided into 3 parts */}
                    <div className="flex gap-2" style={{ width: '40%' }}>
                      {/* Save Button - 1/3 of 40% */}
                      <div className="flex-1">
                        <button
                          onClick={handleSubmit(onSubmit)}
                          disabled={isLoading || !hasChanges()}
                          className="w-full btn-compact-primary"
                          title="Save note"
                        >
                          {isLoading ? (
                            <ButtonSpinner size="sm" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      
                      {/* Delete Button - 1/3 of 40% */}
                      <div className="flex-1">
                        <button
                          onClick={handleDelete}
                          disabled={isDeleting || isLoading}
                          className="w-full btn-compact-danger"
                          title="Delete note"
                        >
                          {isDeleting ? (
                            <ButtonSpinner />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      
                      {/* Close Button - 1/3 of 40% */}
                      <div className="flex-1">
                        {isInTab ? (
                          <button
                            onClick={() => {
                              if (handleNavigateAway({})) {
                                closeTab(tabId)
                              }
                            }}
                            className="w-full btn-compact text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="Close tab"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : (
                          <Link
                            to="/notes"
                            onClick={handleNavigateAway}
                            className="w-full btn-compact text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="Back to notes"
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content Field */}
                <div>
                  
                  <div ref={contentEditorRef}>
                    <Controller
                      name="content"
                      control={control}
                      render={({ field }) => (
                        <RichTextEditor
                          ref={richTextEditorRef}
                          key={`content-${noteId}-${noteData?.lastModified || 'initial'}`}
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="Write your note content here..."
                          disabled={isLoading}
                          className={`rich-text-editor ${errors.content ? 'error' : ''}`}
                          isFullscreen={false}
                        />
                      )}
                    />
                  </div>
                  {errors.content && (
                    <p className="form-error">{errors.content.message}</p>
                  )}
                  <p className="form-help">
                    
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

      </form>

    </div>
  )
}

export default EditNotePage 